import React, { useState, useEffect, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import dynamicInfo from '../../../images/icons/dynamic-info.svg';

import StoryModal from './story-modal';
import Story from './story';
import Icon from '../../ui/icon';
import MocksButton from '../../misc/mocks-button';

import { session } from '../../../services/session';
import { linksService } from './links-service';

const TOO_MANY_LINKS =
  'Over 20 URLs have been detected. Please remove the extra products and then click “Continue”';

const PasteProductUrls = ({ selectedStory, setSelectedStory, oldLinks, setOldLinks, location }) => {
  const [error, setError] = useState(null);
  const [dynamic, setDynamic] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [highlightedUrls, setHighlightedUrls] = useState('');
  const [hasErroredUrls, setHasErroredUrls] = useState(false);
  const [invalidUrls, setInvalidUrls] = useState([]);
  const [numberDetected, setNumberDetected] = useState(0);
  const [showMocks, setShowMocks] = useState(false);

  const ref = useRef(null);
  const history = useHistory();

  useEffect(() => {
    getSelectedStory();
    setNumberDetected(linksService.parseLinks(oldLinks).length);
  }, []);

  useEffect(() => {
    highlight();
  }, [oldLinks, invalidUrls]);

  useEffect(() => {
    if (showMocks) {
      setOldLinks(linksService.getMockPastedLinks());
    }
  }, [showMocks]);

  async function getSelectedStory() {
    if (location) {
      const urlParams = new URLSearchParams(location.search);
      const res = await linksService.getEdit(session.orgId, { edit_id: urlParams.get('edit_id') });
      setSelectedStory(res.data.data[0]);
    }
  }

  function highlight() {
    const splitText = oldLinks.split('\n');

    let foundErroredUrl = false;
    invalidUrls.forEach((url, idx) => {
      const index = splitText.indexOf(url);

      if (index !== -1 && url) {
        splitText.splice(index, 1, <mark key={idx}>{url}</mark>);
        foundErroredUrl = true;
      }
    });
    setHasErroredUrls(foundErroredUrl);

    const textWithNewlines = splitText.map((char, index) =>
      char ? <div key={index}>{char}</div> : <br key={index} />,
    );

    setHighlightedUrls(textWithNewlines);
  }

  function changeMirrorScrollPosition(e) {
    ref.current.scrollTop = e.target.scrollTop;
  }

  async function handleModalSelect(story) {
    const res = await linksService.editStory(session.orgId, story);
    setSelectedStory(res.data.data[0]);
  }

  async function handleContinue() {
    const oldLinksList = linksService.parseLinks(oldLinks);
    const uniqueLinks = removeDuplicates(oldLinksList);
    setOldLinks(uniqueLinks.join('\n\n'));

    const allValid = validateUrls(oldLinksList);

    setNumberDetected(oldLinksList.length);

    if (oldLinksList.length && allValid) {
      setError(null);
      history.push(
        `/v2/publisher/${session.orgId}/links/create-links/validate-links?edit_id=${selectedStory.edit_id}`,
      );
    }
  }

  function removeDuplicates(links) {
    const seen = [];
    links.forEach(link => {
      if (!seen.includes(link)) {
        seen.push(link);
      }
    });
    return seen;
  }

  function validateUrls(links) {
    const invalidUrls = [];

    links.forEach(link => {
      if (!isUrl(link)) {
        invalidUrls.push(link);
      }
    });

    if (invalidUrls.length) {
      setError(
        'Make sure all links are up to date, spelled correctly, and separated by line indents.',
      );
    } else {
      const linkLimit = 20;

      links.forEach((link, index) => {
        if (index >= linkLimit) {
          invalidUrls.push(link);
        }
      });

      if (invalidUrls.length) {
        setError(TOO_MANY_LINKS);
      }
    }

    setInvalidUrls(invalidUrls);

    return !invalidUrls.length;
  }

  function isUrl(link) {
    try {
      new URL(link);
    } catch (e) {
      return false;
    }
    return true;
  }

  function handleChangeTextarea(e) {
    setOldLinks(e.target.value);

    if (error === TOO_MANY_LINKS) {
      setInvalidUrls([]);
    }
  }

  const story = selectedStory ? (
    <Story
      story={selectedStory}
      // handleClick={() => setShowModal(true)} editable={true}
    />
  ) : null;

  const editStoryModal = showModal ? (
    <StoryModal
      story={selectedStory}
      action="edit"
      setShowModal={setShowModal}
      handleSubmit={handleModalSelect}
    />
  ) : null;

  const enabled = selectedStory && oldLinks && !hasErroredUrls;

  const clearButton = oldLinks ? (
    <button className="clear-btn" onClick={() => setOldLinks('')}>
      <span>Clear</span>
      <div className="x-container">
        <Icon iconName="x" size={8} />
      </div>
    </button>
  ) : null;

  const errorMessage = error ? (
    <div className="error">
      <div className="alert-icon-container">
        <Icon iconName="alert" size={15} />
      </div>
      <span className="message">{error}</span>
    </div>
  ) : null;

  return (
    <div className="paste-product-urls">
      {editStoryModal}
      <Link
        className="go-back"
        to={`/v2/publisher/${session.orgId}/links/create-links/select-story`}
      >
        <span className="arrow-left"></span>
        <span className="text">Go Back</span>
      </Link>
      {story}
      <div className="content">
        <div className="header">
          <h2>Paste Product URLs</h2>
          <div className="right-headers">
            <div className="dynamic-links">Dynamic Links</div>
            <img className="info-icon" src={dynamicInfo} alt="info" />
            <div className="switch-container" onClick={() => setDynamic(!dynamic)}>
              <div className={`switch ${dynamic ? 'dynamic' : ''}`}>
                <span></span>
              </div>
            </div>
          </div>
        </div>
        <div className="description">
          Paste your product URLs here. Make sure they’re each on separate lines so we can read
          them.
        </div>
        {errorMessage}
        <div className="links-input-container">
          <div className="quantity-clear">
            <div className={`quantity ${error === TOO_MANY_LINKS ? 'too-many' : ''}`}>
              {numberDetected} of 20
            </div>
            {clearButton}
          </div>
          <textarea
            className={oldLinks ? 'filled' : ''}
            value={oldLinks}
            onChange={handleChangeTextarea}
            onScroll={changeMirrorScrollPosition}
            placeholder="Paste product URLs here..."
          />
          <div className="mirror" ref={ref}>
            {highlightedUrls}
          </div>
        </div>
        <button
          className={`continue-btn ${!enabled ? 'disabled' : ''}`}
          disabled={!enabled}
          onClick={handleContinue}
        >
          Continue
        </button>
        <MocksButton setShowMocks={setShowMocks} showMocks={showMocks} />
      </div>
    </div>
  );
};

export default PasteProductUrls;
